import { tagService as service } from "services/serviceStorage";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { wrapRequest } from "common/utils";
import { BaseApiResponse } from "common/types";
import {
    GridFilter,
    Tag,
    LinksModalStateFilter,
    Link,
    LinkState
} from "./types";

export const fetchTags = createAsyncThunk<{ data: Tag[]; total: number, filter: GridFilter }, GridFilter>(
    'tags/fetchTags',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Tag[]; totalCount: number }>>({
            handler: () => service.getTags(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(s => ({
                ...s,
                key: s.tagId
            })) || [],
            total: data?.result.totalCount || 0,
            filter
        }

        return mappedData;
    }
);

export const fetchTag = createAsyncThunk<Tag, number>(
    'tags/fetchTag',
    async (tagId) => {
        const { data } = await wrapRequest<BaseApiResponse<Tag>>({
            handler: () => service.getTag(tagId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const deleteTags = createAsyncThunk<void, number[]>(
    'tags/deleteTags',
    async (tagIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteTags(tagIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const saveTag = createAsyncThunk<void, Partial<Tag>>(
    'tags/saveTag',
    async (tag, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.saveTag(tag),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// links actions
export const fetchLinks = createAsyncThunk<{ data: Link[]; total: number; filter: Partial<LinksModalStateFilter> }, Partial<LinksModalStateFilter>>(
    'tags/fetchLinks',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Link[]; totalCount: number; }>>({
            handler: () => service.getLinks(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(s => ({
                ...s,
                key: s.scenarioId
            })) || [],
            total: data?.result.totalCount || 0,
            filter
        };

        return mappedData;
    }
);

export const fetchLookup = createAsyncThunk<string[], Partial<LinksModalStateFilter> & { filters: number[] }>(
    'tags/fetchLookup',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getLookup(filter),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const deleteLinks = createAsyncThunk<void, { tagIds: number[], scenarioIds: number[] }>(
    'tags/deleteLinks',
    async ({ tagIds, scenarioIds }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteLinks({ tagIds, scenarioIds }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const saveLinks = createAsyncThunk<void, LinkState['links']>(
    'tags/saveLinks',
    async (links, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.saveLinks(links),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);